import QRCode from 'qrcode';
import JsBarcode from 'jsbarcode';

const PROTOCOLO_FONT = '"Quicksand", "Tahoma", "Helvetica Neue", Arial, sans-serif';

// Fator de super-amostragem. Tudo é desenhado em SCALE× a resolução final,
// resultando num PNG nítido para tela e impressão térmica 80mm @ 203 DPI.
const SCALE = 3;
const s = (v) => v * SCALE;

const loadCanvasImage = (src) => new Promise((resolve, reject) => {
  const image = new Image();
  image.onload = () => resolve(image);
  image.onerror = reject;
  image.src = src;
});

const drawCenteredText = (ctx, text, x, y, maxWidth, fontSize, weight = '400') => {
  const safeText = String(text || '-');
  let size = fontSize;
  do {
    ctx.font = `${weight} ${size}px ${PROTOCOLO_FONT}`;
    if (ctx.measureText(safeText).width <= maxWidth || size <= s(14)) break;
    size -= SCALE;
  } while (size > s(14));
  ctx.fillText(safeText, x, y);
};

const drawWrappedText = (ctx, text, x, y, maxWidth, fontSize, lineHeight, weight = '400') => {
  ctx.font = `${weight} ${fontSize}px ${PROTOCOLO_FONT}`;
  const safe = String(text || '-');
  const words = safe.split(/\s+/);
  const lines = [];
  let current = '';
  for (const word of words) {
    const tentativa = current ? `${current} ${word}` : word;
    if (ctx.measureText(tentativa).width <= maxWidth) {
      current = tentativa;
    } else {
      if (current) lines.push(current);
      current = word;
    }
  }
  if (current) lines.push(current);
  lines.forEach((linha, i) => {
    ctx.fillText(linha, x, y + i * lineHeight);
  });
  return lines.length;
};

const formatDateTimeBr = (raw) => {
  if (!raw) return '-';
  try {
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return String(raw);
    return d.toLocaleString('pt-BR');
  } catch {
    return String(raw);
  }
};

const quantidadeChavesNfe = (dados) => {
  const documentos = Array.isArray(dados?.documentos) ? dados.documentos : [];
  const chavesNosDocumentos = documentos.filter((item) => {
    const tipo = typeof item === 'object' && item !== null
      ? Number(item.documento_tipo ?? item.tipo ?? 1)
      : 1;
    const documento = typeof item === 'string'
      ? item
      : item?.documento ?? item?.chave ?? '';
    return tipo === 1 && String(documento).trim().length > 0;
  }).length;

  if (chavesNosDocumentos > 0) return chavesNosDocumentos;

  const chaves = dados?.chaves_nfe ?? dados?.chavesNfe ?? dados?.chaves;
  if (Array.isArray(chaves) && chaves.length > 0) return chaves.filter(Boolean).length;

  const totalInformado = Number(dados?.quantidade_chaves ?? dados?.quantidade_nfe);
  if (Number.isFinite(totalInformado) && totalInformado > 0) return totalInformado;

  return 1;
};

/**
 * Gera a imagem PNG do protocolo em alta resolução (3× super-amostragem).
 * @param {Object} dados — { protocolo, destinatario_cdfil, destinatario_cdfun,
 *                            nome_funcionario, remetente_cdfun, remetente_cdfil,
 *                            data_cadastro, documentos, quantidade_chaves,
 *                            lojaNomeResolver: (cdfil) => string }
 */
export async function gerarImagemProtocoloDataUrl(dados) {
  if (!dados?.protocolo) throw new Error('Protocolo inválido');

  if (document.fonts && document.fonts.load) {
    try {
      await Promise.all([
        document.fonts.load(`400 ${s(21)}px Quicksand`),
        document.fonts.load(`400 ${s(28)}px Quicksand`),
        document.fonts.load(`700 ${s(34)}px Quicksand`),
        document.fonts.load(`600 ${s(30)}px Quicksand`),
      ]);
      await document.fonts.ready;
    } catch (e) { /* fallback silencioso */ }
  }

  const protocolo = String(dados.protocolo || '').trim();
  const protocoloBarcode = protocolo.padStart(12, '0');
  const lojaNome = typeof dados.lojaNomeResolver === 'function' ? dados.lojaNomeResolver : (() => '');

  const destinoCodigo = dados.destinatario_cdfil || '';
  const destinoNome = lojaNome(destinoCodigo);
  const destinatario = dados.nome_destinatario || dados.destinatario_nome || dados.destinatario_cdfun || '-';
  const volumes = quantidadeChavesNfe(dados);
  const remetenteNome = dados.nome_funcionario ? `${dados.nome_funcionario}` : (dados.remetente_cdfun || '-');
  const remetenteLojaCodigo = dados.remetente_cdfil || '';
  const remetenteLojaNome = lojaNome(remetenteLojaCodigo);
  const remetenteLoja = remetenteLojaCodigo
    ? `${remetenteLojaCodigo}${remetenteLojaNome ? `-${remetenteLojaNome}` : ''}`
    : '-';
  const destinoLinha = destinoCodigo
    ? `${destinoCodigo}${destinoNome ? ` - ${String(destinoNome).toUpperCase()}` : ''}`
    : '-';

  const cardW = s(484);
  const cardH = s(830);
  const PAD = s(12);
  const canvas = document.createElement('canvas');
  canvas.width = cardW + PAD * 2;
  canvas.height = cardH + PAD * 2;
  const ctx = canvas.getContext('2d');

  // Maximiza qualidade de rasterização e suavização de imagens redimensionadas
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.textRendering = 'geometricPrecision';

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  const cardX = PAD;
  const cardY = PAD;
  ctx.strokeStyle = '#111111';
  ctx.lineWidth = s(4);
  ctx.strokeRect(cardX, cardY, cardW, cardH);

  // Logo — desenhada no tamanho proporcional ao card escalado
  const logo = await loadCanvasImage('/callfarma-logo-protocolo.png');
  const logoW = Math.min(cardW - s(54), s(430));
  const logoH = Math.round((logo.height / logo.width) * logoW);
  const logoX = cardX + ((cardW - logoW) / 2);
  const logoY = cardY + s(20);
  ctx.drawImage(logo, logoX, logoY, logoW, logoH);

  const infoTop = logoY + logoH + s(60);

  ctx.fillStyle = '#111111';
  ctx.textAlign = 'center';
  drawCenteredText(ctx, `Destino: ${destinoLinha}`, canvas.width / 2, infoTop, cardW - s(48), s(34), '700');
  drawCenteredText(ctx, `Destinatário: ${destinatario || '-'}`, canvas.width / 2, infoTop + s(32), cardW - s(48), s(21), '400');
  drawCenteredText(ctx, `Protocolo Online ${protocolo}`, canvas.width / 2, infoTop + s(74), cardW - s(48), s(28), '400');
  drawCenteredText(ctx, `Nr. de volumes: ${volumes}`, canvas.width / 2, infoTop + s(101), cardW - s(48), s(19), '400');
  drawCenteredText(ctx, `Criado: ${formatDateTimeBr(dados.data_cadastro)}`, canvas.width / 2, infoTop + s(137), cardW - s(48), s(31), '400');

  // QR — gerado nativamente em alta resolução (sem rescale lossy)
  const QR_SIZE = s(150);
  const qrCanvas = document.createElement('canvas');
  await QRCode.toCanvas(qrCanvas, protocolo, {
    width: QR_SIZE,
    margin: 1,
    errorCorrectionLevel: 'M',
    color: { dark: '#000000', light: '#ffffff' }
  });
  ctx.drawImage(qrCanvas, (canvas.width - QR_SIZE) / 2, infoTop + s(158), QR_SIZE, QR_SIZE);

  // Barcode — gerado em resolução nativa do canvas final, sem interpolação
  const BAR_W = s(330);
  const BAR_H = s(95);
  const barcodeCanvas = document.createElement('canvas');
  JsBarcode(barcodeCanvas, protocoloBarcode, {
    format: 'CODE128',
    width: SCALE * 2.2,   // largura do bit-stripe em px no canvas auxiliar (escala completa)
    height: BAR_H,
    displayValue: false,
    margin: 0,
    background: '#ffffff',
    lineColor: '#000000'
  });
  // pixel-perfect: temporariamente desativa smoothing pra preservar as bordas verticais
  ctx.imageSmoothingEnabled = false;
  ctx.drawImage(barcodeCanvas, (canvas.width - BAR_W) / 2, infoTop + s(332), BAR_W, BAR_H);
  ctx.imageSmoothingEnabled = true;

  ctx.fillStyle = '#111111';
  ctx.font = `600 ${s(30)}px ${PROTOCOLO_FONT}`;
  ctx.fillText(protocoloBarcode, canvas.width / 2, infoTop + s(460));

  ctx.textAlign = 'left';
  const remetenteFontSize = s(21);
  const remetenteLineHeight = s(26);
  const remetenteMaxWidth = cardW - s(56);
  ctx.font = `400 ${remetenteFontSize}px ${PROTOCOLO_FONT}`;
  const remetenteStartY = infoTop + s(525);
  const linhasRemetente = drawWrappedText(
    ctx,
    `Remetente: ${String(remetenteNome).toUpperCase()}`,
    cardX + s(28),
    remetenteStartY,
    remetenteMaxWidth,
    remetenteFontSize,
    remetenteLineHeight,
    '400'
  );

  const remLojaY = remetenteStartY + linhasRemetente * remetenteLineHeight + s(8);
  drawWrappedText(
    ctx,
    `Rem. Loja: ${String(remetenteLoja).toUpperCase()}`,
    cardX + s(28),
    remLojaY,
    remetenteMaxWidth,
    remetenteFontSize,
    remetenteLineHeight,
    '400'
  );

  return canvas.toDataURL('image/png');
}

export function imprimirProtocoloDataUrl(dataUrl) {
  if (!dataUrl) return;
  const printWindow = window.open('', '_blank', 'width=420,height=700');
  if (!printWindow) {
    alert('Permita pop-ups para imprimir.');
    return;
  }
  const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>Imprimir Protocolo</title>
  <style>
    @page { size: 80mm auto; margin: 0; }
    html, body { margin: 0; padding: 0; background: #fff; }
    body { width: 80mm; display: flex; justify-content: center; padding: 2mm 0 6mm; }
    img { width: 76mm; height: auto; display: block; image-rendering: -webkit-optimize-contrast; image-rendering: crisp-edges; }
    @media print { body { padding: 0; } }
  </style>
</head>
<body>
  <img id="protocolo" src="${dataUrl}" alt="Protocolo" />
  <script>
    const img = document.getElementById('protocolo');
    const trigger = () => { setTimeout(() => { window.focus(); window.print(); }, 120); };
    if (img.complete) trigger();
    else img.addEventListener('load', trigger);
    window.addEventListener('afterprint', () => window.close());
  </script>
</body>
</html>`;
  printWindow.document.open();
  printWindow.document.write(html);
  printWindow.document.close();
}

export function baixarProtocoloDataUrl(dataUrl, filename = 'protocolo.png') {
  if (!dataUrl) return;
  const link = document.createElement('a');
  link.href = dataUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}
